package org.example.zone360.models

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class ClubWidgetResponse(
    val clubAverages: ClubAverages = ClubAverages(),
    val totalSessions: Long = 0,
)

@Serializable
data class ClubAverages(
    @SerialName("coach_talk_ratio")
    val coachTalkRatio: CoachTalkRatio = CoachTalkRatio(),

    @SerialName("cognitive_training")
    val cognitiveTraining: CognitiveTraining = CognitiveTraining(),

    @SerialName("competitive_environment")
    val competitiveEnvironment: CompetitiveEnvironment = CompetitiveEnvironment(),

    @SerialName("development_orientation")
    val developmentOrientation: DevelopmentOrientation = DevelopmentOrientation(),

    @SerialName("game_realism")
    val gameRealism: GameRealism = GameRealism(),

    @SerialName("match_analysis")
    val matchAnalysis: MatchAnalysis = MatchAnalysis(),

    @SerialName("methodology_consistency")
    val methodologyConsistency: MethodologyConsistency = MethodologyConsistency(),

    @SerialName("session_intensity")
    val sessionIntensity: SessionIntensity = SessionIntensity(),

    @SerialName("tactical_complexity")
    val tacticalComplexity: TacticalComplexity = TacticalComplexity(),

    @SerialName("topic_diversity")
    val topicDiversity: TopicDiversity = TopicDiversity(),
)

@Serializable
data class CoachTalkRatio(
    val score: Long = 0,
)

@Serializable
data class CognitiveTraining(
    val score: Long = 0,

    @SerialName("sub_scores")
    val subScores: CognitiveTrainingSubScores = CognitiveTrainingSubScores(),
)

@Serializable
data class CognitiveTrainingSubScores(
    @SerialName("decision_making")
    val decisionMaking: Long = 0,

    val perception: Long = 0,

    @SerialName("problem_solving")
    val problemSolving: Long = 0,

    val scanning: Long = 0,

    @SerialName("tactical_awareness")
    val tacticalAwareness: Long = 0,
)

@Serializable
data class CompetitiveEnvironment(
    val score: Long = 0,

    @SerialName("sub_scores")
    val subScores: CompetitiveEnvironmentSubScores = CompetitiveEnvironmentSubScores(),
)

@Serializable
data class CompetitiveEnvironmentSubScores(
    val accountability: Long = 0,
    val competition: Long = 0,

    @SerialName("scoring_systems")
    val scoringSystems: Long = 0,

    @SerialName("time_pressure")
    val timePressure: Long = 0,

    @SerialName("winning_conditions")
    val winningConditions: Long = 0,
)

@Serializable
data class DevelopmentOrientation(
    val score: Long = 0,
)

@Serializable
data class GameRealism(
    val score: Long = 0,

    @SerialName("sub_scores")
    val subScores: GameRealismSubScores = GameRealismSubScores(),
)

@Serializable
data class GameRealismSubScores(
    @SerialName("decision_making")
    val decisionMaking: Long = 0,

    @SerialName("directional_play")
    val directionalPlay: Long = 0,

    @SerialName("match_pressure")
    val matchPressure: Long = 0,

    val opposition: Long = 0,
    val transitions: Long = 0,
)
@Serializable
data class MatchAnalysis(
    @SerialName("root_causes")
    val rootCauses: List<String> = emptyList(),

    val score: Long = 0,

    val strengths: List<String> = emptyList(),

    @SerialName("sub_scores")
    val subScores: MatchAnalysisSubScores = MatchAnalysisSubScores(),

    @SerialName("training_priorities")
    val trainingPriorities: List<TrainingPriority> = emptyList(),

    val weaknesses: List<String> = emptyList(),
)

@Serializable
data class MatchAnalysisSubScores(
    @SerialName("in_possession")
    val inPossession: Long = 0,

    @SerialName("out_of_possession")
    val outOfPossession: Long = 0,

    val transitions: Long = 0,
)


@Serializable
data class TrainingPriority(
    val area: String = "",
    val percentage: Long = 0,
)

@Serializable
data class MethodologyConsistency(
    @SerialName("consistent_principles")
    val consistentPrinciples: List<String> = emptyList(),

    val score: Long = 0,
)
@Serializable
data class SessionIntensity(
    val score: Long = 0,
)

@Serializable
data class TacticalComplexity(
    val score: Long = 0,

    @SerialName("top_concepts")
    val topConcepts: List<String> = emptyList(),
)

@Serializable
data class TopicDiversity(
    val score: Long = 0,

    @SerialName("topics_covered")
    val topicsCovered: List<TopicsCovered> = emptyList(),
)

@Serializable
data class TopicsCovered(
    val percentage: Long,
    val topic: String,
)

@Serializable
data class RecentTrend(
    @SerialName("coach_talk_ratio")
    val coachTalkRatio: List<WidgetTrend> = emptyList(),

    @SerialName("cognitive_training")
    val cognitiveTraining: List<WidgetTrend> = emptyList(),

    @SerialName("competitive_environment")
    val competitiveEnvironment: List<WidgetTrend> = emptyList(),

    @SerialName("development_orientation")
    val developmentOrientation: List<WidgetTrend> = emptyList(),

    @SerialName("game_realism")
    val gameRealism: List<WidgetTrend> = emptyList(),

    @SerialName("match_analysis")
    val matchAnalysis: List<WidgetTrend> = emptyList(),

    @SerialName("methodology_consistency")
    val methodologyConsistency: List<WidgetTrend> = emptyList(),

    @SerialName("session_intensity")
    val sessionIntensity: List<WidgetTrend> = emptyList(),

    @SerialName("tactical_complexity")
    val tacticalComplexity: List<WidgetTrend> = emptyList(),

    @SerialName("topic_diversity")
    val topicDiversity: List<WidgetTrend> = emptyList(),
)

@Serializable
data class WidgetTrend(
    val score: Long = 0,
    val sessionDateTime: String = "",

    @SerialName("sessionID")
    val sessionId: String = "",
)